/**
 * Centralized API base URLs for the web app.
 * - Main (Go) API: VITE_MAIN_API_URL, default /api/v1 (Vite proxy → :8080 in dev)
 * - Advanced (Node) API: VITE_ADVANCED_API_URL, default /api (Vite proxy → :8080 in dev)
 * Legacy VITE_MAIN_API_URLI_URL is interpreted when the new vars are unset (see plan).
 */

function trimEnv(v: string | undefined): string | undefined {
  const t = v?.trim();
  return t || undefined;
}

/** True when legacy VITE_MAIN_API_URLI_URL targets advanced-features (port 3001), not the Go API. */
function isLegacyAdvancedOnlyUrl(url: string): boolean {
  if (url.includes('/api/v1')) return false;
  return url.includes(':3001') || url.includes('localhost:3001');
}

/** Primary REST client (Go): paths like /auth/login, /apps, … */
export function getMainApiBaseUrl(): string {
  const explicit = trimEnv(import.meta.env.VITE_MAIN_API_URL);
  if (explicit) return explicit.replace(/\/$/, '');

  const legacy = trimEnv(import.meta.env.VITE_MAIN_API_URL);
  if (legacy) {
    if (isLegacyAdvancedOnlyUrl(legacy)) {
      /* ignore for main */
    } else if (legacy.includes('/api/v1')) {
      return legacy.replace(/\/$/, '');
    } else if (legacy.startsWith('/')) {
      return legacy.replace(/\/$/, '');
    } else if (legacy.startsWith('http://') || legacy.startsWith('https://')) {
      try {
        const u = new URL(legacy);
        if (u.pathname.includes('/api/v1')) return legacy.replace(/\/$/, '');
        return `${u.origin}/api/v1`.replace(/\/$/, '');
      } catch {
        /* ignore */
      }
    }
  }
  return '/api/v1';
}

/** Advanced-features REST client (Express): paths like /ai-assistant/… */
export function getAdvancedApiBaseUrl(): string {
  const explicit = trimEnv(import.meta.env.VITE_ADVANCED_API_URL);
  if (explicit) return explicit.replace(/\/$/, '');

  const main = trimEnv(import.meta.env.VITE_MAIN_API_URL);
  if (main) {
    if (main.startsWith('http://') || main.startsWith('https://')) {
      try {
        const u = new URL(main);
        return `${u.origin}/api`;
      } catch {
        /* ignore */
      }
    }
    if (main.includes('/api/v1')) {
      return main.replace('/api/v1', '/api').replace(/\/$/, '');
    }
  }

  const legacy = trimEnv(import.meta.env.VITE_MAIN_API_URLI_URL);
  if (legacy && !legacy.includes('/api/v1')) {
    if (legacy.includes('localhost:3001') || legacy.includes(':3001')) {
      let b = legacy.replace(/\/$/, '');
      if (!b.endsWith('/api')) b = `${b}/api`;
      return b;
    }
    if (legacy.startsWith('http://') || legacy.startsWith('https://')) {
      try {
        const u = new URL(legacy);
        return `${u.origin}/api`;
      } catch {
        /* ignore */
      }
    }
  }
  return '/api';
}

/** Socket.IO origin for the advanced-features server (no path suffix). */
export function getAdvancedWsUrl(): string {
  const explicit = trimEnv(import.meta.env.VITE_ADVANCED_WS_URL);
  if (explicit) return explicit.replace(/\/$/, '');

  const base = getAdvancedApiBaseUrl();
  try {
    if (base.startsWith('http://') || base.startsWith('https://')) {
      const u = new URL(base);
      return `${u.protocol}//${u.host}`;
    }
  } catch {
    /* ignore */
  }

  const legacyWs = trimEnv(import.meta.env.VITE_WS_URL);
  if (legacyWs) return legacyWs.replace(/\/$/, '');

  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return 'http://localhost:8080';
}

/** ws/wss base for WebSockets that should follow the page host (dev: Vite proxies HTTP /api only). */
export function getBrowserWsBaseUrl(): string {
  const legacyWs = trimEnv(import.meta.env.VITE_WS_URL);
  if (legacyWs) {
    const w = legacyWs.replace(/\/$/, '');
    if (w.startsWith('ws://') || w.startsWith('wss://')) return w;
    if (w.startsWith('http://')) return `ws://${w.slice('http://'.length)}`;
    if (w.startsWith('https://')) return `wss://${w.slice('https://'.length)}`;
  }

  const mainApi = getMainApiBaseUrl();
	// Using Vite proxy for WebSockets since ws: true is configured.
  if (typeof window !== 'undefined' && window.location) {
    return window.location.protocol === 'https:'
      ? `wss://${window.location.host}`
      : `ws://${window.location.host}`;
  }
  return 'ws://127.0.0.1:8080';
}
